import { Editor } from 'editor-widget';
import { I18nThemed } from 'interfaces/mixins';
import { lccstore } from 'lcc-state';
import { logQuiz } from 'logger';
import timesync from 'timesync';

import { beforeProperties } from '@dojo/framework/core/decorators/beforeProperties';
import { DNode, WidgetProperties } from '@dojo/framework/core/interfaces';
import { theme } from '@dojo/framework/core/mixins/Themed';
import { v, w } from '@dojo/framework/core/vdom';
import { Messages } from '@dojo/framework/i18n/i18n';
import Button from '@dojo/widgets/button';
import Dialog from '@dojo/widgets/dialog';

import css from '../css/discussion.m.css';
import { IResult, InfoRPC, QFResultData, QRLocator, Question, QuestionTypeProperties } from '../interfaces';

const ISSEEN = 0;

export interface MemoProperties extends QuestionTypeProperties {
	question: Question;
	quizkind: string;
	info: InfoRPC;
	quizid: string;
	localizedBundle: { isPlaceholder: boolean; format: any; messages: Messages };
}

@theme(css)
export class Memo extends I18nThemed<MemoProperties> {
	private handles: { destroy?: () => void; remove?: () => void }[] = [];
	private areyousure: boolean = false;
	private _propsInited: boolean = false;
	private data: string = '';

	private getData: () => string = () => this.data;

	protected onAttach(): void {
		const h1 = lccstore.onChange(lccstore.path('dynls'), () => this.invalidate());
		this.handles.push(h1);
	}

	protected onDetach(): void {
		this.handles.forEach((h) => {
			h.destroy && h.destroy();
			h.remove && h.remove();
		});
	}

	@beforeProperties()
	ensure_nls(properties: MemoProperties & WidgetProperties): MemoProperties & WidgetProperties {
		const { get, path } = lccstore;
		const dynls = get(path('dynls'));
		const bundle = dynls && dynls.get('quiz');
		if (!bundle) {
			properties.localizedBundle = { isPlaceholder: true, format: null, messages: {} as Messages };
		} else {
			properties.localizedBundle = this.localizeBundle(bundle);
		}
		return properties;
	}

	protected update_result(answer?: string, postIt: boolean = false): Promise<any> {
		const { question, updater } = this.properties;
		const { icode = {}, status, cursor } = question;

		question.flags[ISSEEN] = true;

		const icodeAnswer: any = answer !== undefined ? answer : this.getData();
		const data: QFResultData = {
			answer: icodeAnswer,
			icode: { ...icode, _answer: icodeAnswer },
			starttime: question.starttime!,
			stoptime: timesync.now().toJSON(),
			isseen: true,
			ismarked: question.marked ? true : false
		};
		const qrlocator: QRLocator = {
			status: postIt ? 'posted' : status,
			cursor,
			qrid: '',
			quiz: ''
		};
		const result: IResult = { qrlocator, data };
		return updater([result]);
	}

	protected render_buttons(): DNode[] {
		const {
			theme,
			localizedBundle: {
				messages: { btnSave_close = 'Save and close', btnSubmit = 'Submit' }
			},
			quizid
		} = this.properties;

		return [
			v('div', { xqa: 'action single', classes: [this.theme(css.oneaction)] }, [
				w(
					Button,
					{
						theme,
						key: 'save',
						onClick: () => {
							this.update_result().then(() => {
								window.history.back();
							});
							logQuiz.info(() => `save and close ${quizid}`);
						}
					},
					[btnSave_close]
				),
				w(
					Button,
					{
						theme,
						key: 'submit',
						onClick: () => {
							this.areyousure = true;
							this.invalidate();
						}
					},
					[btnSubmit]
				)
			])
		];
	}

	protected areYouSure(): DNode {
		const {
			onFinish,
			quizid,
			localizedBundle: {
				messages: { ays_title: title = '', ays_body = '', ays_cancel = '', ays_submit = '' }
			}
		} = this.properties;

		return w(Dialog, { open: this.areyousure, onRequestClose: () => (this.areyousure = false) }, [
			{
				title,
				content: v('div', { classes: [this.theme(css.areYouSure)] }, [
					v('div', { key: '1', classes: [this.theme(css.sureBody)] }, [ays_body]),
					v('div', { key: '2', classes: [this.theme(css.sureButtons)] }, [
						w(
							Button,
							{
								key: 'ays-finish',
								onClick: () => {
									this.update_result(undefined, true).then(() => {
										onFinish();
									});
									logQuiz.info(() => `submit ${quizid}`);
								}
							},
							[ays_submit]
						),
						w(
							Button,
							{
								key: 'ays-cancel',
								onClick: () => {
									this.areyousure = false;
									this.invalidate();
								}
							},
							[ays_cancel]
						)
					])
				])
			}
		]);
	}

	protected render_completed(): DNode {
		const {
			question,
			localizedBundle: {
				messages: { memoComplete = '"memoComplete" missing in dynls' }
			}
		} = this.properties;
		const { id, answertext } = question;

		return v('div', { key: id, xqa: `memo ${id}`, classes: [this.theme(css.root)] }, [
			v('h4', { key: 'response-head', classes: [this.theme(css.question)], innerHTML: memoComplete }),
			v('div', { key: 'response-body', classes: [this.theme(css.completedMemo)], innerHTML: answertext })
		]);
	}

	protected render_question(): DNode {
		const {
			question,
			localizedBundle: {
				messages: { discInstructions }
			}
		} = this.properties;
		const { id, answertext } = question;

		return v('div', { key: id, xqa: `memo ${id}`, classes: [this.theme(css.root)] }, [
			v('div', { key: 'question-text', classes: [this.theme(css.question)], innerHTML: discInstructions }),
			w<typeof Editor>('Editor', {
				content: answertext,
				onContent: (content: string) => {
					this.data = content;
				}
			}),
			v('div', { key: 'action-block', xqa: 'action block', classes: [this.theme(css.actions)] }, [
				v(
					'div',
					{ key: 'submit-action', xqa: 'action submit', classes: [this.theme(css.oneaction)] },
					this.render_buttons()
				)
			]),
			this.areYouSure()
		]);
	}

	protected render(): DNode {
		const { isPlaceholder } = this.properties.localizedBundle;
		if (isPlaceholder) {
			return null;
		}
		const { question, info } = this.properties;
		if (!question || !info) {
			return null;
		}
		const { start_state } = info;

		switch (start_state) {
			case 'question':
				return this.render_question();

			case 'posted':
			case 'graded':
				return this.render_completed();

			default:
				return null;
		}
	}

	@beforeProperties()
	protected stateProperties(properties: MemoProperties & WidgetProperties): MemoProperties & WidgetProperties {
		const { quizstore } = properties;
		const { get, path } = quizstore;

		if (!this._propsInited) {
			const paths = [['question'], ['quiz'], ['loaded'], ['info']];
			const invalidatePaths = paths.map((path: any) => quizstore.path(path.join('/')));
			const handle = quizstore.onChange(invalidatePaths, () => this.invalidate());
			this.own({
				destroy: () => {
					handle.remove();
				}
			});
			this._propsInited = true;
		}

		const quizdata = get(path('quiz'));
		properties.question = get(path('question'));
		properties.info = get(path('info'));
		properties.loaded = get(path('loaded'));
		properties.quizkind = quizdata.kind;
		properties.quizid = quizdata.id;

		return properties;
	}
}
